<script lang="ts">
	import { nanoid } from "nanoid";
	import P from "#lib/components/P.svelte";
	import TextDivider from "#lib/components/TextDivider.svelte";
	import type { Schema } from "#lib/components/jsonForms/jsonSchema.js";
	import type { FieldOption } from "#lib/components/page/components/form/conditionals/ConditionCard.svelte";
	import ValueRuleEditor from "#lib/components/page/components/form/conditionals/ValueRuleEditor.svelte";
	import Button from "#lib/ui/Button/index.js";
	import type { ClassValue } from "svelte/elements";

	interface Props {
		schema: string;
		fields: FieldOption[];
		class?: ClassValue;
		usedFields: string[];
		onChange: (s: string) => void;
	}

	let { schema = $bindable(), fields, usedFields = $bindable([]), onChange, ...props }: Props = $props();
	let jsonSchema = $derived.by(() => {
		const s = $state(JSON.parse(schema) as Schema);
		return s;
	});
	let lastError: unknown = $state();
	$effect(() => {
		if (lastError) {
			return;
		}
		if (jsonSchema.anyOf !== undefined && !jsonSchema.anyOf.length) {
			delete jsonSchema.anyOf;
		}
	});
	$effect(() => {
		const keys = jsonSchema.anyOf?.flatMap(or => or.allOf?.flatMap(and => (and.properties ? Object.keys(and.properties) : undefined)))?.filter(v => Boolean(v)) as string[] | undefined;
		usedFields = keys || [];
	});
	function deleteAnd(or: Schema, andIndex: number, orIndex: number) {
		if (or.allOf!.length > 1) {
			or.allOf!.splice(andIndex, 1);
		} else {
			jsonSchema.anyOf!.splice(orIndex, 1);
		}
		updateSchema(jsonSchema);
	}

	function createAnd(or: Schema) {
		or.allOf?.push({ title: nanoid(6) });
	}

	function initializeSchema() {
		jsonSchema.anyOf = [{ title: nanoid(6), allOf: [{ title: nanoid(6) }] }];
		updateSchema(jsonSchema);
	}

	function addOrGroup() {
		jsonSchema.anyOf?.push({ title: nanoid(6), allOf: [{ title: nanoid(6) }] });
	}

	function updateSchema(s: Schema) {
		const json = JSON.stringify(s);
		if (json === schema) return;
		onChange(json);
	}

	function onValueRuleChange(orIndex: number, andIndex: number, schema: Schema) {
		jsonSchema!.anyOf![orIndex].allOf![andIndex]! = schema;
		updateSchema(jsonSchema);
	}
</script>

<div class={props.class}>
	<div class="mb-2 flex w-full items-center justify-between">
		<P weight="semibold" color="text-gray-900">Rule</P>
		<Button size="md" class="shrink-0 hover:cursor-pointer hover:underline" color="none" data-action-name="forms-add-or-group" onclick={addOrGroup}>+ Add "or" group</Button>
	</div>
	{#if jsonSchema.anyOf}
		{#each jsonSchema.anyOf as or, orIndex (or.title)}
			{#if orIndex !== 0}
				<TextDivider text="OR" />
			{/if}
			{#each or.allOf! as and, andIndex (and.title)}
				<ValueRuleEditor {fields} onChange={s => onValueRuleChange(orIndex, andIndex, s)} index={andIndex} schema={jsonSchema!.anyOf![orIndex].allOf![andIndex]!} class="mb-4" showDelete={!(jsonSchema.anyOf.length === 1 && or.allOf!.length === 1)} showCreate={andIndex === or.allOf!.length - 1} bind:lastError onCreate={() => createAnd(or)} onDelete={() => deleteAnd(or, andIndex, orIndex)} />
			{/each}
		{/each}
	{:else}
		<Button size="sm" data-action-name="forms-add-new-or" color="alternative" onclick={initializeSchema}>Add rule</Button>
	{/if}
</div>
